import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { designSystem } from '@yola/ws-ui';
import { t } from '../../localization';
import { POPOVER_WIDTH, ANIMATED_ICONS } from './constants'; // Import constants from the constants file

const { ActionButton, BlinkingIcon, Popover, PopoverContent, PopoverTrigger, Paragraph } =
  designSystem;

const getCaptions = () => ({
  buttonLabel: t('Professional services'),
  popoverTitle: t('Request website changes'),
  popoverDescription: t('Send a request and your designer will update your site for you'),
});

// eslint-disable-next-line yola/react/prop-types
const ButtonComponent = ({ buttonLabel, isAnimated, href, onClick }) => (
  <ActionButton
    format="text"
    label={buttonLabel}
    {...(isAnimated && { appearance: 'accent' })}
    {...(!isAnimated && { fontWeight: 'regular' })}
    {...(onClick && { onClick })}
    {...(href && { href })}
  />
);

const DifmButton = ({ onClick = null, href = null, isAnimated = false, withPopover = false }) => {
  const captions = getCaptions();
  const isTouchDevice = bowser.mobile || bowser.tablet;
  const hasPopover = withPopover && !isTouchDevice;

  const handleClick = useCallback(() => {
    if (!onClick) return;

    if (!isAnimated) {
      Popover.hide();
      onClick();
    } else {
      onClick();
    }
  }, [onClick, isAnimated]);

  if (isAnimated) {
    return (
      <div className="ws-difm-button">
        <ButtonComponent
          buttonLabel={captions.buttonLabel}
          isAnimated={isAnimated}
          href={href}
          {...(onClick && { onClick: handleClick })}
        />
        {ANIMATED_ICONS.map(({ key, ...restProps }) => (
          <BlinkingIcon key={key} {...restProps} />
        ))}
      </div>
    );
  }

  if (!hasPopover) {
    return (
      <ButtonComponent
        buttonLabel={captions.buttonLabel}
        isAnimated={isAnimated}
        href={href}
        {...(onClick && { onClick: handleClick })}
      />
    );
  }

  return (
    <Popover
      width={POPOVER_WIDTH}
      placement="bottom-center"
      appearance="dark"
      title={captions.popoverTitle}
    >
      <PopoverTrigger>
        <ButtonComponent
          buttonLabel={captions.buttonLabel}
          isAnimated={isAnimated}
          href={href}
          {...(onClick && { onClick: handleClick })}
        />
      </PopoverTrigger>
      <PopoverContent>
        <Paragraph align="left" appearance="low-emphasis" size="medium">
          {captions.popoverDescription}
        </Paragraph>
      </PopoverContent>
    </Popover>
  );
};

DifmButton.propTypes = {
  onClick: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  href: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  withPopover: PropTypes.bool,
  isAnimated: PropTypes.bool,
};

export default DifmButton;
